import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { AnnualTimetableCell, Grade } from '../../types/domain';
import { useCalendarLinkedContext } from '../../hooks/useCalendarLinkedContext';
import { saveClassLessonAlarmCustomSchedule, getClassLessonAlarmCustomSchedule } from '../../services/classLessonAlarmCustomScheduleService';
import { diagnoseClassLessonAlarm } from '../../utils/classLessonAlarmDiagnose';
import {
  applyClassLessonAlarmCustomSchedule,
  classLessonAlarmBlockKey,
  createDefaultClassLessonAlarmCustomSchedule,
  seedCustomOverridesFromTargets,
  type ClassLessonAlarmCustomSchedule,
  type ClassLessonAlarmScheduleMode,
} from '../../utils/classLessonAlarmCustomSchedule';
import { peekClassLessonAlarmCustomSchedule } from '../../utils/classLessonAlarmCustomScheduleCache';
import { ensureClassLessonAlarmData } from '../../utils/classLessonAlarmSchedule';
import { toISODate } from '../../utils/date';
import {
  CLASS_LESSON_ALARM_OFFSET_OPTIONS,
  CLASS_LESSON_ALARM_SOUND_OPTIONS,
  CLASS_LESSON_ALARM_TEST_FIRE_EVENT,
  createDefaultClassLessonAlarmSettings,
  loadClassLessonAlarmSettings,
  saveClassLessonAlarmSettings,
  type ClassLessonAlarmChannelSettings,
  type ClassLessonAlarmOffset,
  type ClassLessonAlarmSettings,
  type ClassLessonAlarmSoundKind,
  type ClassLessonAlarmTestFireDetail,
} from '../../utils/classLessonAlarmSettings';
import { canShowClassLessonAlarmDesktopNotice } from '../../utils/classLessonAlarmCrossTab';
import ClassLessonAlarmPreviewBlock from './ClassLessonAlarmPreviewBlock';

const ClockIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path
      d="M3 4.5L6 7.5L9 4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClassLessonAlarmChannelEditor: React.FC<{
  title: string;
  value: ClassLessonAlarmChannelSettings;
  onChange: (next: ClassLessonAlarmChannelSettings) => void;
  disabled?: boolean;
}> = ({ title, value, onChange, disabled = false }) => {
  const [soundOpen, setSoundOpen] = useState(false);
  const soundLabel =
    CLASS_LESSON_ALARM_SOUND_OPTIONS.find((o) => o.value === value.soundKind)?.label ?? value.soundKind;
  const controlsDisabled = disabled;

  useEffect(() => {
    if (controlsDisabled) setSoundOpen(false);
  }, [controlsDisabled]);

  return (
    <section
      className={`cp-class-lesson-alarm-channel${disabled ? ' is-disabled' : ''}`}
      data-tutorial-alarm={title === '일반수업 알람' ? 'channel-regular' : undefined}
    >
      <div className="cp-class-lesson-alarm-channel-head">
        <h3 className="cp-class-lesson-alarm-channel-title">{title}</h3>
      </div>

      <div className="cp-class-lesson-alarm-kind">
        <div className="cp-class-lesson-alarm-kind-select">
          <button
            type="button"
            className="cp-class-lesson-alarm-kind-trigger"
            disabled={controlsDisabled}
            aria-haspopup="listbox"
            aria-expanded={soundOpen}
            aria-label={`${title} 알람 종류`}
            onClick={() => setSoundOpen((open) => !open)}
          >
            <span className="cp-class-lesson-alarm-kind-trigger-text">{soundLabel}</span>
            <ChevronIcon />
          </button>
          {soundOpen ? (
            <>
              <div
                className="cp-class-lesson-alarm-kind-overlay"
                aria-hidden
                onClick={() => setSoundOpen(false)}
              />
              <ul className="cp-class-lesson-alarm-kind-menu" role="listbox">
                {CLASS_LESSON_ALARM_SOUND_OPTIONS.map((opt) => (
                  <li key={opt.value} role="option" aria-selected={value.soundKind === opt.value}>
                    <button
                      type="button"
                      className={`cp-class-lesson-alarm-kind-option${
                        value.soundKind === opt.value ? ' is-selected' : ''
                      }`}
                      onClick={() => {
                        onChange({ ...value, soundKind: opt.value as ClassLessonAlarmSoundKind });
                        setSoundOpen(false);
                      }}
                    >
                      {opt.label}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </div>

      <div className="cp-class-lesson-alarm-offset">
        <div className="cp-class-lesson-alarm-offset-btns" role="radiogroup" aria-label={`${title} 알람 시각`}>
          {CLASS_LESSON_ALARM_OFFSET_OPTIONS.map((opt) => {
            const selected = value.offset === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={controlsDisabled}
                className={`cp-class-lesson-alarm-offset-btn${selected ? ' is-selected' : ''}`}
                onClick={() => onChange({ ...value, offset: opt.value as ClassLessonAlarmOffset })}
              >
                <ClockIcon />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
}

function parseISODateLocal(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 12, 0, 0, 0);
}

function formatPreviewTitle(previewDate: Date, realTodayIso: string): string {
  const m = previewDate.getMonth() + 1;
  const d = previewDate.getDate();
  const isToday = toISODate(previewDate) === realTodayIso;
  return isToday ? `${m}월 ${d}일 오늘 알람` : `${m}월 ${d}일 알람`;
}

const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'];

function formatCustomLoadedTitle(previewDate: Date): string {
  const m = previewDate.getMonth() + 1;
  const d = previewDate.getDate();
  const w = WEEKDAY_KO[previewDate.getDay()] ?? '';
  return `사용자 설정 알람(${m}월 ${d}일 ${w}요일)`;
}

const ClassLessonAlarmSettingsModal: React.FC<{
  open: boolean;
  workspaceId: string;
  grades: Grade[];
  /** TopNav에서 선택된 클래스 — 이 반의 알람만 미리보기·등록 */
  currentGradeId?: string | null;
  onClose: () => void;
}> = ({ open, workspaceId, grades, currentGradeId, onClose }) => {
  const selectedGradeId = (currentGradeId ?? '').trim();
  const selectedGrades = useMemo(() => {
    if (!selectedGradeId) return [] as Grade[];
    return grades.filter((g) => g.id === selectedGradeId);
  }, [grades, selectedGradeId]);
  const calendarGradeId = selectedGradeId || (grades[0]?.id ?? '').trim();
  const calendarCtx = useCalendarLinkedContext(workspaceId, calendarGradeId);
  const programSidebarAcademicYear = calendarCtx.academicYear;
  const [draft, setDraft] = useState<ClassLessonAlarmSettings>(() =>
    createDefaultClassLessonAlarmSettings()
  );
  const [notifyPermission, setNotifyPermission] = useState<NotificationPermission | 'unsupported'>(
    () => (canShowClassLessonAlarmDesktopNotice() ? Notification.permission : 'unsupported')
  );
  const [tick, setTick] = useState(0);
  const [scheduleEpoch, setScheduleEpoch] = useState(0);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [resolvedTerm, setResolvedTerm] = useState<{ academicYear: number; termNumber: 1 | 2 } | null>(
    null
  );
  const [ensuredCellsByKey, setEnsuredCellsByKey] = useState<Record<string, AnnualTimetableCell>>({});
  const [previewDateIso, setPreviewDateIso] = useState(() => toISODate(new Date()));
  /** 커스텀 탭이 마지막으로 불러온 날짜 — 탭 전환과 무관하게 유지 */
  const [customDateIso, setCustomDateIso] = useState(() => toISODate(new Date()));
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [testSoundKind, setTestSoundKind] = useState<ClassLessonAlarmSoundKind>('basic');
  const [testSoundOpen, setTestSoundOpen] = useState(false);
  const [scheduleTab, setScheduleTab] = useState<ClassLessonAlarmScheduleMode>('auto');
  const [customDraft, setCustomDraft] = useState<ClassLessonAlarmCustomSchedule>(() =>
    createDefaultClassLessonAlarmCustomSchedule()
  );
  const [saving, setSaving] = useState(false);
  const [savingCustom, setSavingCustom] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [customSaveMessage, setCustomSaveMessage] = useState<string | null>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  /** 커스텀: 다른 날짜 로드 시 자동 스케줄의 활성/비활성까지 시드 예약 */
  const pendingCustomSeedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const loaded = loadClassLessonAlarmSettings(workspaceId);
    setDraft({
      ...loaded,
      regular: { ...loaded.regular, enabled: true },
      dedicated: { ...loaded.dedicated, enabled: true },
    });
    const cached = peekClassLessonAlarmCustomSchedule(workspaceId);
    setCustomDraft(cached);
    setScheduleTab(cached.mode === 'custom' ? 'custom' : 'auto');
    setPreviewDateIso(toISODate(new Date()));
    setCustomDateIso(toISODate(new Date()));
    setDatePickerOpen(false);
    setTestSoundKind('basic');
    setTestSoundOpen(false);
    setSaveError(null);
    setCustomSaveMessage(null);
    let cancelled = false;
    void getClassLessonAlarmCustomSchedule(workspaceId, { forceRefresh: true })
      .then((schedule) => {
        if (cancelled) return;
        setCustomDraft(schedule);
        setScheduleTab(schedule.mode === 'custom' ? 'custom' : 'auto');
      })
      .catch(() => {
        /* LS 캐시 유지 */
      });
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (datePickerOpen) {
          setDatePickerOpen(false);
          return;
        }
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, datePickerOpen]);

  useEffect(() => {
    if (!open) return undefined;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  useEffect(() => {
    if (!open || !workspaceId.trim() || !draft.masterEnabled) return undefined;
    if (!selectedGrades.length) {
      setScheduleLoading(false);
      setEnsuredCellsByKey({});
      setResolvedTerm(null);
      return undefined;
    }
    let cancelled = false;
    const previewNow = parseISODateLocal(previewDateIso);
    setScheduleLoading(true);
    setEnsuredCellsByKey({});
    void ensureClassLessonAlarmData({
      workspaceId,
      grades: selectedGrades,
      now: previewNow,
      programSidebarAcademicYear,
    })
      .then((ensured) => {
        if (cancelled) return;
        setResolvedTerm({ academicYear: ensured.academicYear, termNumber: ensured.termNumber });
        setEnsuredCellsByKey(ensured.cellsByKey);
      })
      .finally(() => {
        if (cancelled) return;
        setScheduleLoading(false);
        setScheduleEpoch((n) => n + 1);
      });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    workspaceId,
    selectedGrades,
    draft.masterEnabled,
    previewDateIso,
    programSidebarAcademicYear,
  ]);

  const realNow = useMemo(() => new Date(), [tick, open]);
  const realTodayIso = toISODate(realNow);

  const diagnosis = useMemo(() => {
    if (!open || !selectedGrades.length) return null;
    const previewDay = parseISODateLocal(previewDateIso);
    const previewNow = new Date(
      previewDay.getFullYear(),
      previewDay.getMonth(),
      previewDay.getDate(),
      realNow.getHours(),
      realNow.getMinutes(),
      realNow.getSeconds()
    );
    return diagnoseClassLessonAlarm({
      workspaceId,
      grades: selectedGrades,
      academicYear: resolvedTerm?.academicYear,
      termNumber: resolvedTerm?.termNumber,
      settings: draft,
      now: previewNow,
      cellsByKey: ensuredCellsByKey,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tick/scheduleEpoch refresh preview
  }, [
    open,
    workspaceId,
    selectedGrades,
    draft,
    tick,
    scheduleEpoch,
    resolvedTerm,
    ensuredCellsByKey,
    previewDateIso,
    realNow,
  ]);

  const autoFirePlan = diagnosis?.firePlan ?? [];

  const displayFirePlan = useMemo(() => {
    if (scheduleTab !== 'custom') return autoFirePlan;
    const asTargets = autoFirePlan.map((p) => ({
      time: p.startTime,
      offset: p.offset,
      soundKind: p.soundKind,
      channel: p.channel,
      gradeId: p.gradeId,
      gradeLabel: p.gradeLabel,
      slotIndex: p.slotIndex,
      periodLabel: p.periodLabel,
      hasLesson: p.hasLesson,
      willRing: p.willRing,
    }));
    const applied = applyClassLessonAlarmCustomSchedule(asTargets, {
      ...customDraft,
      mode: 'custom',
    });
    return applied.map((t) => ({
      startTime: t.time,
      channel: t.channel,
      channelLabel: t.channel === 'dedicated' ? '전담' : '일반',
      offset: t.offset,
      offsetLabel:
        CLASS_LESSON_ALARM_OFFSET_OPTIONS.find((o) => o.value === t.offset)?.label ?? t.offset,
      soundKind: t.soundKind,
      soundLabel:
        CLASS_LESSON_ALARM_SOUND_OPTIONS.find((o) => o.value === t.soundKind)?.label ?? t.soundKind,
      gradeId: t.gradeId,
      gradeLabel: t.gradeLabel,
      slotIndex: t.slotIndex,
      periodLabel: t.periodLabel,
      hasLesson: t.hasLesson,
      willRing: t.willRing,
    }));
  }, [autoFirePlan, customDraft, scheduleTab]);

  /** 다른 날짜 로드 완료 시, 해당 일자 자동 스케줄의 활성/비활성 상태로 커스텀 시드 */
  useEffect(() => {
    if (scheduleTab !== 'custom') return;
    if (!pendingCustomSeedRef.current) return;
    if (scheduleLoading) return;
    pendingCustomSeedRef.current = false;
    const overrides: ClassLessonAlarmCustomSchedule['overrides'] = {};
    for (const p of autoFirePlan) {
      overrides[classLessonAlarmBlockKey(p.gradeId, p.slotIndex)] = {
        time: p.startTime,
        offset: p.offset,
        channel: p.channel,
        soundKind: p.soundKind,
        enabled: p.willRing,
      };
    }
    setCustomDraft((cur) => ({ ...cur, overrides }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scheduleEpoch로 최신 autoFirePlan 반영
  }, [scheduleTab, scheduleLoading, scheduleEpoch]);

  if (!open) return null;

  const buildSeedTargetsFromAuto = () =>
    autoFirePlan.map((p) => ({
      time: p.startTime,
      offset: p.offset,
      soundKind: p.soundKind,
      channel: p.channel,
      gradeId: p.gradeId,
      gradeLabel: p.gradeLabel,
      slotIndex: p.slotIndex,
      periodLabel: p.periodLabel,
      hasLesson: p.hasLesson,
      willRing: p.willRing,
    }));

  const ensureCustomOverrides = () =>
    Object.keys(customDraft.overrides).length > 0
      ? customDraft.overrides
      : seedCustomOverridesFromTargets(buildSeedTargetsFromAuto());

  const handleSave = () => {
    if (saving || savingCustom) return;
    setSaveError(null);
    setCustomSaveMessage(null);
    saveClassLessonAlarmSettings(workspaceId, draft);
    const toSave: ClassLessonAlarmCustomSchedule = {
      v: 1,
      /** 모달 최종 저장: 선택 탭이 실제 울림 모드 */
      mode: scheduleTab,
      overrides: ensureCustomOverrides(),
    };
    setSaving(true);
    void saveClassLessonAlarmCustomSchedule(workspaceId, toSave)
      .then(() => {
        setCustomDraft(toSave);
        onClose();
      })
      .catch((e) => {
        setSaveError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setSaving(false));
  };

  /** 커스텀 스케줄 내용만 저장 — 활성 모드(auto/custom)는 모달 최종 저장에서 결정 */
  const handleSaveCustomSettings = () => {
    if (saving || savingCustom) return;
    setSaveError(null);
    setCustomSaveMessage(null);
    const toSave: ClassLessonAlarmCustomSchedule = {
      v: 1,
      mode: customDraft.mode,
      overrides: ensureCustomOverrides(),
    };
    setSavingCustom(true);
    void saveClassLessonAlarmCustomSchedule(workspaceId, toSave)
      .then(() => {
        setCustomDraft(toSave);
        setCustomSaveMessage('커스텀 알람 설정을 저장했습니다.');
      })
      .catch((e) => {
        setSaveError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setSavingCustom(false));
  };

  const handleResetCustom = () => {
    setCustomSaveMessage(null);
    /** 초기화: 오늘 날짜 기준 기본 블럭을 모두(활성) 표시 — 시드 예약 취소 */
    pendingCustomSeedRef.current = false;
    setPreviewDateIso(toISODate(startOfLocalDay(new Date())));
    setCustomDateIso(toISODate(startOfLocalDay(new Date())));
    setCustomDraft((cur) => ({
      v: 1,
      mode: cur.mode,
      overrides: {},
    }));
  };

  const updateCustomBlock = (
    gradeId: string,
    slotIndex: number,
    patch: Partial<{
      time: string;
      offset: ClassLessonAlarmOffset;
      channel: 'regular' | 'dedicated';
      soundKind: ClassLessonAlarmSoundKind;
      enabled: boolean;
    }>
  ) => {
    const key = classLessonAlarmBlockKey(gradeId, slotIndex);
    setCustomSaveMessage(null);
    setCustomDraft((cur) => {
      const auto = autoFirePlan.find((p) => p.gradeId === gradeId && p.slotIndex === slotIndex);
      const prev = cur.overrides[key] ?? {
        time: auto?.startTime ?? '09:00',
        offset: auto?.offset ?? '3M',
        channel: auto?.channel ?? 'regular',
        soundKind: auto?.soundKind ?? 'basic',
        enabled: true,
      };
      return {
        ...cur,
        overrides: {
          ...cur.overrides,
          [key]: {
            time: patch.time ?? prev.time,
            offset: patch.offset ?? prev.offset,
            channel: patch.channel ?? prev.channel,
            soundKind: patch.soundKind ?? prev.soundKind,
            enabled: typeof patch.enabled === 'boolean' ? patch.enabled : prev.enabled,
          },
        },
      };
    });
  };

  const masterOn = draft.masterEnabled !== false;
  const presentationOnlyOn = draft.presentationOnly === true;
  const desktopNoticeOn = notifyPermission === 'granted';
  const handleDesktopNoticeToggle = () => {
    if (notifyPermission === 'unsupported') {
      window.alert('이 브라우저는 바탕화면 알림을 지원하지 않습니다.');
      return;
    }
    if (notifyPermission === 'granted') {
      window.alert(
        '바탕화면 알림이 허용되어 있습니다.\n끄려면 브라우저 주소창 왼쪽 사이트 설정에서 알림을 차단하세요.'
      );
      return;
    }
    if (notifyPermission === 'denied') {
      window.alert(
        '바탕화면 알림이 차단되어 있습니다.\n브라우저 주소창 왼쪽 사이트 설정에서 알림을 허용한 뒤 다시 시도하세요.'
      );
      return;
    }
    void Notification.requestPermission().then((p) => setNotifyPermission(p));
  };
  const previewDate = parseISODateLocal(previewDateIso);
  const titleText = formatPreviewTitle(previewDate, realTodayIso);

  return createPortal(
    <div
      className="cp-class-lesson-alarm-settings-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cp-class-lesson-alarm-settings-title"
      data-tutorial-alarm="settings-root"
    >
      <div className="cp-class-lesson-alarm-settings-dim" aria-hidden onClick={onClose} />
      <div className="cp-class-lesson-alarm-settings-panel" onClick={(e) => e.stopPropagation()}>
        <div className="cp-class-lesson-alarm-settings-header">
          <h2 id="cp-class-lesson-alarm-settings-title" className="cp-class-lesson-alarm-settings-title">
            수업 알람 설정
          </h2>
          <div className="cp-class-lesson-alarm-settings-header-actions">
            <div
              className="cp-class-lesson-alarm-master-toggle-wrap"
              title="클래스 플래너 창이 모두 최소화·가려져 있어도 알람 시각에 바탕화면 알림을 띄웁니다. 알림을 누르면 창으로 돌아옵니다."
            >
              <span className="cp-class-lesson-alarm-master-toggle-label">바탕화면 알림</span>
              <button
                type="button"
                className={`team-auto-approve-toggle cp-class-lesson-alarm-master-toggle${
                  desktopNoticeOn ? ' on' : ''
                }`}
                aria-pressed={desktopNoticeOn}
                aria-label="바탕화면 알림 허용"
                onClick={handleDesktopNoticeToggle}
              >
                <span className="team-auto-approve-toggle-slider" data-on={!desktopNoticeOn}>
                  OFF
                </span>
                <span className="team-auto-approve-toggle-slider" data-on={desktopNoticeOn}>
                  ON
                </span>
              </button>
            </div>
            <div
              className="cp-class-lesson-alarm-master-toggle-wrap"
              title="메인 프레젠테이션 탭이 열려 있으면 알람 팝업을 프레젠테이션에만 표시합니다."
            >
              <span className="cp-class-lesson-alarm-master-toggle-label">프레젠테이션 전용</span>
              <button
                type="button"
                className={`team-auto-approve-toggle cp-class-lesson-alarm-master-toggle${
                  presentationOnlyOn ? ' on' : ''
                }`}
                aria-pressed={presentationOnlyOn}
                aria-label="프레젠테이션 전용 표시 켜기/끄기"
                onClick={() =>
                  setDraft((cur) => ({ ...cur, presentationOnly: !cur.presentationOnly }))
                }
              >
                <span className="team-auto-approve-toggle-slider" data-on={!presentationOnlyOn}>
                  OFF
                </span>
                <span className="team-auto-approve-toggle-slider" data-on={presentationOnlyOn}>
                  ON
                </span>
              </button>
            </div>
            <div className="cp-class-lesson-alarm-master-toggle-wrap">
              <span className="cp-class-lesson-alarm-master-toggle-label">알람</span>
              <button
                type="button"
                className={`team-auto-approve-toggle cp-class-lesson-alarm-master-toggle${
                  masterOn ? ' on' : ''
                }`}
                aria-pressed={masterOn}
                aria-label="수업 알람 켜기/끄기"
                data-tutorial-alarm="master-toggle"
                onClick={() => setDraft((cur) => ({ ...cur, masterEnabled: !cur.masterEnabled }))}
              >
                <span className="team-auto-approve-toggle-slider" data-on={!masterOn}>
                  OFF
                </span>
                <span className="team-auto-approve-toggle-slider" data-on={masterOn}>
                  ON
                </span>
              </button>
            </div>
            <button
              type="button"
              className="cp-class-lesson-alarm-settings-close"
              onClick={onClose}
              aria-label="닫기"
            >
              <img src="/modal-close.svg" alt="" width={18} height={18} />
            </button>
          </div>
        </div>
        <div className="cp-class-lesson-alarm-settings-body">
          <div className="cp-class-lesson-alarm-settings-cols">
            <section className="cp-class-lesson-alarm-settings-col is-options" aria-label="알람 설정">
              <section
                className="cp-class-lesson-alarm-section-block"
                aria-labelledby="cp-class-lesson-alarm-immediate-title"
              >
                <h3
                  id="cp-class-lesson-alarm-immediate-title"
                  className="cp-class-lesson-alarm-section-block-title"
                >
                  즉시 알람 실행
                </h3>
                <div
                  className="cp-class-lesson-alarm-test-fire"
                  role="group"
                  aria-label="즉시 알람 실행"
                  data-tutorial-alarm="test-fire-group"
                >
                  <div className="cp-class-lesson-alarm-test-sound">
                    <span className="cp-class-lesson-alarm-section-field-label">알람 종류</span>
                    <div className="cp-class-lesson-alarm-kind-select">
                      <button
                        type="button"
                        className="cp-class-lesson-alarm-kind-trigger"
                        disabled={!masterOn}
                        aria-haspopup="listbox"
                        aria-expanded={testSoundOpen}
                        aria-label="즉시 실행 알람 종류"
                        onClick={() => setTestSoundOpen((v) => !v)}
                      >
                        <span className="cp-class-lesson-alarm-kind-trigger-text">
                          {CLASS_LESSON_ALARM_SOUND_OPTIONS.find((o) => o.value === testSoundKind)?.label ??
                            testSoundKind}
                        </span>
                        <ChevronIcon />
                      </button>
                      {testSoundOpen ? (
                        <>
                          <div
                            className="cp-class-lesson-alarm-kind-overlay"
                            aria-hidden
                            onClick={() => setTestSoundOpen(false)}
                          />
                          <ul className="cp-class-lesson-alarm-kind-menu" role="listbox">
                            {CLASS_LESSON_ALARM_SOUND_OPTIONS.map((opt) => (
                              <li
                                key={opt.value}
                                role="option"
                                aria-selected={testSoundKind === opt.value}
                              >
                                <button
                                  type="button"
                                  className={`cp-class-lesson-alarm-kind-option${
                                    testSoundKind === opt.value ? ' is-selected' : ''
                                  }`}
                                  onClick={() => {
                                    setTestSoundKind(opt.value);
                                    setTestSoundOpen(false);
                                  }}
                                >
                                  {opt.label}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : null}
                    </div>
                  </div>
                  {(
                    [
                      { offset: '1M' as const, label: '1분 전 즉시 실행' },
                      { offset: '3M' as const, label: '3분 전 즉시 실행' },
                      { offset: '5M' as const, label: '5분 전 즉시 실행' },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={opt.offset}
                      type="button"
                      className="cp-class-lesson-alarm-test-fire-btn"
                      data-tutorial-alarm="test-fire"
                      disabled={!masterOn}
                      onClick={() => {
                        const detail: ClassLessonAlarmTestFireDetail = {
                          workspaceId,
                          offset: opt.offset,
                          soundKind: testSoundKind,
                        };
                        window.dispatchEvent(
                          new CustomEvent(CLASS_LESSON_ALARM_TEST_FIRE_EVENT, { detail })
                        );
                        onClose();
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </section>

              <section
                className="cp-class-lesson-alarm-section-block"
                aria-labelledby="cp-class-lesson-alarm-config-title"
              >
                <h3
                  id="cp-class-lesson-alarm-config-title"
                  className="cp-class-lesson-alarm-section-block-title"
                >
                  알람 설정
                </h3>
                <div className="cp-class-lesson-alarm-section-block-body">
                  <ClassLessonAlarmChannelEditor
                    title="일반수업 알람"
                    value={draft.regular}
                    disabled={!masterOn}
                    onChange={(regular) => setDraft((cur) => ({ ...cur, regular }))}
                  />
                  <ClassLessonAlarmChannelEditor
                    title="전담수업 알람"
                    value={draft.dedicated}
                    disabled={!masterOn}
                    onChange={(dedicated) => setDraft((cur) => ({ ...cur, dedicated }))}
                  />
                </div>
              </section>
            </section>

            <section className="cp-class-lesson-alarm-settings-col is-schedule" aria-label="오늘 알람 스케줄">
              <div className="cp-class-lesson-alarm-schedule-tabs" role="tablist" aria-label="알람 스케줄 모드">
                <button
                  type="button"
                  role="tab"
                  aria-selected={scheduleTab === 'auto'}
                  className={`cp-class-lesson-alarm-schedule-tab${scheduleTab === 'auto' ? ' is-active' : ''}`}
                  onClick={() => {
                    setScheduleTab('auto');
                    /** 자동 알람은 항상 오늘 날짜 기준 */
                    setPreviewDateIso(toISODate(startOfLocalDay(new Date())));
                    setDatePickerOpen(false);
                  }}
                >
                  자동 알람 설정
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={scheduleTab === 'custom'}
                  className={`cp-class-lesson-alarm-schedule-tab${
                    scheduleTab === 'custom' ? ' is-active' : ''
                  }`}
                  onClick={() => {
                    setScheduleTab('custom');
                    /** 커스텀 탭이 마지막으로 보던 날짜·편집값 복원 */
                    setPreviewDateIso(customDateIso);
                    setDatePickerOpen(false);
                  }}
                >
                  커스텀 알람 설정
                </button>
              </div>
              <div className="cp-class-lesson-alarm-schedule-head">
                {scheduleTab === 'custom' ? (
                  <div className="cp-class-lesson-alarm-schedule-title-wrap">
                    <button
                      type="button"
                      className="cp-class-lesson-alarm-schedule-title-btn cp-class-lesson-alarm-schedule-custom-title"
                      onClick={() => setDatePickerOpen((v) => !v)}
                      title="알람을 불러올 날짜 선택"
                      aria-expanded={datePickerOpen}
                    >
                      {previewDateIso === realTodayIso
                        ? '사용자 설정 알람'
                        : formatCustomLoadedTitle(previewDate)}
                    </button>
                    {datePickerOpen ? (
                      <div className="cp-class-lesson-alarm-date-popover">
                        <label className="cp-class-lesson-alarm-date-popover-label">
                          불러올 날짜
                          <input
                            ref={dateInputRef}
                            type="date"
                            className="cp-class-lesson-alarm-date-input"
                            value={previewDateIso}
                            onChange={(e) => {
                              const next = (e.target.value || '').trim();
                              if (!next) return;
                              setPreviewDateIso(next);
                              setCustomDateIso(next);
                              setCustomSaveMessage(null);
                              /** 선택 일자의 스케줄(활성/비활성 포함)을 로드 후 시드 */
                              pendingCustomSeedRef.current = true;
                              setCustomDraft((cur) => ({ ...cur, overrides: {} }));
                              setDatePickerOpen(false);
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          className="cp-class-lesson-alarm-date-today"
                          onClick={() => {
                            setPreviewDateIso(toISODate(startOfLocalDay(new Date())));
                            setCustomDateIso(toISODate(startOfLocalDay(new Date())));
                            setCustomSaveMessage(null);
                            /** 오늘 스케줄(활성/비활성 포함)을 로드 후 시드 */
                            pendingCustomSeedRef.current = true;
                            setCustomDraft((cur) => ({ ...cur, overrides: {} }));
                            setDatePickerOpen(false);
                          }}
                        >
                          오늘로
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <span className="cp-class-lesson-alarm-schedule-plain-title">{titleText}</span>
                )}
                {scheduleTab !== 'custom' && diagnosis ? (
                  <span className="cp-class-lesson-alarm-schedule-now">
                    {scheduleLoading ? '불러오는 중… ' : null}
                    {diagnosis.weekdayLabel}
                    {previewDateIso === realTodayIso ? ` · ${diagnosis.nowLabel.slice(11)}` : null}
                  </span>
                ) : null}
              </div>
              {!masterOn ? (
                <p className="cp-class-lesson-alarm-diagnose-empty">알람이 꺼져 있어 블록을 만들지 않습니다.</p>
              ) : (
                <>
                  {diagnosis?.blockers.length ? (
                    <ul className="cp-class-lesson-alarm-diagnose-blockers">
                      {diagnosis.blockers
                        .filter((b) => !b.includes('꺼져 있습니다'))
                        .map((b) => (
                          <li key={b}>{b}</li>
                        ))}
                    </ul>
                  ) : null}
                  {displayFirePlan.length > 0 ? (
                    <div className="cp-class-lesson-alarm-edu-list">
                      {displayFirePlan.map((p) => (
                        <ClassLessonAlarmPreviewBlock
                          key={`${p.gradeId}-${p.slotIndex}-${p.periodLabel}`}
                          item={{
                            startTime: p.startTime,
                            channel: p.channel,
                            channelLabel: p.channelLabel,
                            offset: p.offset,
                            offsetLabel: p.offsetLabel,
                            soundKind: p.soundKind,
                            soundLabel: p.soundLabel,
                            gradeId: p.gradeId,
                            gradeLabel: p.gradeLabel,
                            slotIndex: p.slotIndex,
                            periodLabel: p.periodLabel,
                            hasLesson: p.hasLesson,
                            willRing: p.willRing,
                          }}
                          editable={scheduleTab === 'custom'}
                          disabled={!masterOn}
                          onChangeTime={(time) => updateCustomBlock(p.gradeId, p.slotIndex, { time })}
                          onChangeOffset={(offset) =>
                            updateCustomBlock(p.gradeId, p.slotIndex, { offset })
                          }
                          onChangeChannel={(channel) =>
                            updateCustomBlock(p.gradeId, p.slotIndex, { channel })
                          }
                          onChangeSoundKind={(soundKind) =>
                            updateCustomBlock(p.gradeId, p.slotIndex, { soundKind })
                          }
                          onToggleEnabled={() =>
                            updateCustomBlock(p.gradeId, p.slotIndex, { enabled: !p.willRing })
                          }
                        />
                      ))}
                    </div>
                  ) : !scheduleLoading &&
                    diagnosis &&
                    diagnosis.blockers.filter((b) => !b.includes('꺼져')).length === 0 ? (
                    <p className="cp-class-lesson-alarm-diagnose-empty">해당 날짜에 울릴 대상이 없습니다.</p>
                  ) : null}
                  {scheduleTab === 'custom' ? (
                    <div className="cp-class-lesson-alarm-custom-reset-row">
                      <div className="cp-class-lesson-alarm-custom-actions">
                        <button
                          type="button"
                          className="cp-btn-secondary cp-class-lesson-alarm-custom-reset-btn"
                          disabled={!masterOn || displayFirePlan.length === 0 || savingCustom}
                          onClick={handleResetCustom}
                        >
                          초기화
                        </button>
                        <button
                          type="button"
                          className="cp-btn-primary cp-class-lesson-alarm-custom-save-btn"
                          disabled={!masterOn || displayFirePlan.length === 0 || savingCustom || saving}
                          onClick={handleSaveCustomSettings}
                        >
                          {savingCustom ? '저장 중…' : '설정 저장'}
                        </button>
                      </div>
                      {customSaveMessage ? (
                        <p className="cp-class-lesson-alarm-custom-save-ok" role="status">
                          {customSaveMessage}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </section>
          </div>
        </div>
        <div className="cp-class-lesson-alarm-settings-footer-area">
          {saveError ? (
            <p className="cp-class-lesson-alarm-settings-save-error" role="alert">
              {saveError}
            </p>
          ) : null}
          <div className="cp-modal-footer">
            <button type="button" className="cp-btn-secondary" onClick={onClose} disabled={saving}>
              닫기
            </button>
            <button
              type="button"
              className="cp-btn-primary"
              data-tutorial-alarm="footer-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '저장 중…' : '저장'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ClassLessonAlarmSettingsModal;
